<script setup>
import { ref, computed, onMounted, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { debounce } from '@chatwoot/utils';
import { useCompaniesStore } from 'dashboard/stores/companies';
import Icon from 'dashboard/components-next/icon/Icon.vue';

import CompaniesListLayout from 'dashboard/components-next/Companies/CompaniesListLayout.vue';
import CompaniesCard from 'dashboard/components-next/Companies/CompaniesCard/CompaniesCard.vue';

import {
  LIFECYCLE_STAGES,
  COMPANY_SIZES,
  DEFAULT_SECTORS,
} from '../constants/companyConstants';

const DEFAULT_SORT_FIELD = 'name';
const DEBOUNCE_DELAY = 300;

const companiesStore = useCompaniesStore();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();

const companies = computed(() => companiesStore.getCompaniesList);
const meta = computed(() => companiesStore.getMeta);
const uiFlags = computed(() => companiesStore.getUIFlags);

const searchQuery = computed(() => route.query?.search || '');
const searchValue = ref(searchQuery.value);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { companies_sort_by: companySortBy = DEFAULT_SORT_FIELD } =
  uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } =
  parseSortSettings(companySortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const activeSort = computed(() => sortState.activeSort);
const activeOrdering = computed(() => sortState.activeOrdering);

const isFetchingList = computed(() => uiFlags.value.fetchingList);

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const sortParam = computed(() => buildSortAttr());

const updateURLParams = (page, search = '', sort = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
  };

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  if (sort) {
    query.sort = sort;
  } else {
    delete query.sort;
  }

  router.replace({ query });
};

const fetchCompanies = async (page, search, sort) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  const currentSort = sort ?? sortParam.value;

  // Only update URL if arguments were explicitly provided
  if (page !== undefined || search !== undefined || sort !== undefined) {
    updateURLParams(currentPage, currentSearch, currentSort);
  }

  if (currentSearch) {
    await companiesStore.search({
      search: currentSearch,
      page: currentPage,
      sort: currentSort,
    });
  } else {
    await companiesStore.get({
      page: currentPage,
      sort: currentSort,
    });
  }
};

const onSearch = debounce(query => {
  searchValue.value = query;
  fetchCompanies(1, query, sortParam.value);
}, DEBOUNCE_DELAY);

const onPageChange = page => {
  fetchCompanies(page, searchValue.value, sortParam.value);
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    companies_sort_by: buildSortAttr(),
  });

  fetchCompanies(1, searchValue.value, buildSortAttr());
};

// Create modal
const showCreateModal = ref(false);
const isFetchingCnpj = ref(false);
const isCreating = ref(false);

const emptyForm = () => ({
  name: '',
  cnpj: '',
  domain: '',
  phone: '',
  website: '',
  sector: '',
  companySize: '',
  lifecycleStage: 'lead',
  annualRevenue: '',
  description: '',
  razaoSocial: '',
  nomeFantasia: '',
  naturezaJuridica: '',
  email: '',
  endereco: '',
  bairro: '',
  cidade: '',
  uf: '',
  cep: '',
  capitalSocial: '',
  dataAbertura: '',
  situacaoCadastral: '',
  dataSituacaoCadastral: '',
  porteReceita: '',
  cnaePrincipal: '',
  cnaesSecundarios: '',
  telefone2: '',
  quadroSocietario: '',
  customAttributes: [],
});
const createForm = ref(emptyForm());

const openCreateModal = () => {
  createForm.value = emptyForm();
  showCreateModal.value = true;
};
const closeCreateModal = () => {
  showCreateModal.value = false;
};

const formatCnpj = raw => {
  const d = raw.replace(/\D/g, '');
  if (d.length !== 14) return raw;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

const buildAddress = data => {
  const parts = [data.logradouro, data.numero, data.complemento].filter(Boolean).join(', ');
  const city = [data.municipio, data.uf].filter(Boolean).join('/');
  return [parts, data.bairro, city, data.cep].filter(Boolean).join(' - ');
};

const applyCnpjToForm = (data, form) => {
  form.cnpj = formatCnpj(String(data.cnpj));
  if (data.nome_fantasia) {
    form.name = data.nome_fantasia;
  } else if (data.razao_social) {
    form.name = data.razao_social;
  }
  if (data.ddd_telefone_1) {
    form.phone = `+55${data.ddd_telefone_1.replace(/\D/g, '')}`;
  }
  if (data.email && !form.domain && data.email.includes('@')) {
    form.domain = data.email.split('@')[1];
  }
  const porteMap = { 'MEI': 'mei', 'MICRO EMPRESA': 'micro', 'EMPRESA DE PEQUENO PORTE': 'pequena' };
  if (data.porte && porteMap[data.porte]) {
    form.companySize = porteMap[data.porte];
  }
  if (data.capital_social && !form.annualRevenue) {
    form.annualRevenue = data.capital_social;
  }

  // Campos diretos do CNPJ (colunas do banco)
  if (data.razao_social) form.razaoSocial = data.razao_social;
  if (data.nome_fantasia) form.nomeFantasia = data.nome_fantasia;
  if (data.natureza_juridica) form.naturezaJuridica = data.natureza_juridica;
  if (data.email) form.email = data.email;

  const endereco = buildAddress(data);
  if (endereco) form.endereco = endereco;
  if (data.bairro) form.bairro = data.bairro;
  if (data.municipio) form.cidade = data.municipio;
  if (data.uf) form.uf = data.uf;
  if (data.cep) form.cep = data.cep;

  if (data.capital_social) form.capitalSocial = data.capital_social;
  if (data.data_inicio_atividade) form.dataAbertura = data.data_inicio_atividade;
  if (data.situacao_cadastral) form.situacaoCadastral = data.descricao_situacao_cadastral || String(data.situacao_cadastral);
  if (data.data_situacao_cadastral) form.dataSituacaoCadastral = data.data_situacao_cadastral;
  if (data.porte) form.porteReceita = data.porte;

  if (data.cnae_fiscal) form.cnaePrincipal = `${data.cnae_fiscal} - ${data.cnae_fiscal_descricao || ''}`;
  if (data.cnaes_secundarios?.length) {
    form.cnaesSecundarios = data.cnaes_secundarios
      .map(c => `${c.codigo} - ${c.descricao}`)
      .join('; ');
  }

  if (data.ddd_telefone_2) form.telefone2 = `+55${data.ddd_telefone_2.replace(/\D/g, '')}`;
  if (data.qsa?.length) {
    form.quadroSocietario = data.qsa
      .map(s => `${s.nome_socio} (${s.qualificacao_socio})`)
      .join('; ');
  }
};

const fetchCnpjForCreate = async () => {
  const digits = createForm.value.cnpj.replace(/\D/g, '');
  if (digits.length !== 14) {
    useAlert('CNPJ deve ter 14 dígitos');
    return;
  }
  isFetchingCnpj.value = true;
  try {
    const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${digits}`);
    if (!res.ok) throw new Error('CNPJ não encontrado');
    const data = await res.json();
    applyCnpjToForm(data, createForm.value);
    useAlert('Dados do CNPJ carregados');
  } catch {
    useAlert('Não foi possível buscar o CNPJ');
  } finally {
    isFetchingCnpj.value = false;
  }
};

const submitCreate = async () => {
  if (!createForm.value.name) return;
  isCreating.value = true;
  try {
    const newCompany = await companiesStore.createCompany({
      name: createForm.value.name,
      cnpj: createForm.value.cnpj,
      domain: createForm.value.domain,
      phone: createForm.value.phone,
      website: createForm.value.website,
      sector: createForm.value.sector,
      company_size: createForm.value.companySize,
      annual_revenue: createForm.value.annualRevenue || null,
      description: createForm.value.description,
      razao_social: createForm.value.razaoSocial,
      nome_fantasia: createForm.value.nomeFantasia,
      natureza_juridica: createForm.value.naturezaJuridica,
      email: createForm.value.email,
      endereco: createForm.value.endereco,
      bairro: createForm.value.bairro,
      cidade: createForm.value.cidade,
      uf: createForm.value.uf,
      cep: createForm.value.cep,
      capital_social: createForm.value.capitalSocial || null,
      data_abertura: createForm.value.dataAbertura,
      situacao_cadastral: createForm.value.situacaoCadastral,
      data_situacao_cadastral: createForm.value.dataSituacaoCadastral,
      porte_receita: createForm.value.porteReceita,
      cnae_principal: createForm.value.cnaePrincipal,
      cnaes_secundarios: createForm.value.cnaesSecundarios,
      telefone_2: createForm.value.telefone2,
      quadro_societario: createForm.value.quadroSocietario,
      custom_attributes: Object.fromEntries(
        (createForm.value.customAttributes || [])
          .filter(a => a.key.trim())
          .map(a => [a.key.trim(), a.value])
      ),
    });
    useAlert(t('COMPANIES.DETAIL.CREATE_SUCCESS'));
    showCreateModal.value = false;
    // Navigate to the new company
    if (newCompany?.id) {
      router.push({
        name: 'companies_detail',
        params: { accountId: route.params.accountId, companyId: newCompany.id },
      });
    } else {
      fetchCompanies(1);
    }
  } catch {
    useAlert(t('COMPANIES.DETAIL.CREATE_ERROR'));
  } finally {
    isCreating.value = false;
  }
};

onMounted(() => {
  searchValue.value = searchQuery.value;
  fetchCompanies();
});
</script>

<template>
  <CompaniesListLayout
    :search-value="searchValue"
    :header-title="t('COMPANIES.HEADER')"
    :current-page="pageNumber"
    :total-items="Number(meta.totalCount || 0)"
    :active-sort="activeSort"
    :active-ordering="activeOrdering"
    :is-fetching-list="isFetchingList"
    :show-pagination-footer="!!companies.length"
    @update:current-page="onPageChange"
    @update:sort="handleSort"
    @search="onSearch"
    @create="openCreateModal"
  >
    <div v-if="isFetchingList" class="flex items-center justify-center p-8">
      <span class="text-n-slate-11 text-base">{{
        t('COMPANIES.LOADING')
      }}</span>
    </div>
    <div
      v-else-if="companies.length === 0"
      class="flex items-center justify-center p-8"
    >
      <span class="text-n-slate-11 text-base">{{
        t('COMPANIES.EMPTY_STATE.TITLE')
      }}</span>
    </div>
    <div v-else class="flex flex-col gap-4 p-4">
      <CompaniesCard
        v-for="company in companies"
        :id="company.id"
        :key="company.id"
        :name="company.name"
        :domain="company.domain"
        :contacts-count="company.contactsCount || 0"
        :description="company.description"
        :avatar-url="company.avatarUrl"
        :updated-at="company.updatedAt"
        :lifecycle-stage="company.lifecycleStage"
        :sector="company.sector"
      />
    </div>
  </CompaniesListLayout>

  <!-- Create Company Modal -->
  <Teleport to="body">
    <div
      v-if="showCreateModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      @click.self="closeCreateModal"
    >
      <div
        class="bg-n-solid-2 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4"
      >
        <div class="flex items-center justify-between px-6 py-4 border-b border-n-slate-3">
          <h2 class="text-lg font-semibold text-n-slate-12">Nova Empresa</h2>
          <button
            class="p-1 rounded-lg hover:bg-n-slate-3 transition-colors"
            @click="closeCreateModal"
          >
            <span class="i-lucide-x size-5 text-n-slate-11" />
          </button>
        </div>

        <div class="p-6 space-y-5">
          <!-- Dados gerais -->
          <div>
            <h3 class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider mb-3">Dados gerais</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Nome *</label>
                <input
                  v-model="createForm.name"
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">CNPJ</label>
                <div class="flex gap-2">
                  <input
                    v-model="createForm.cnpj"
                    type="text"
                    placeholder="XX.XXX.XXX/XXXX-XX"
                    class="flex-1 px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                  />
                  <button
                    type="button"
                    class="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-n-iris-3 text-n-iris-11 hover:bg-n-iris-4 transition-colors whitespace-nowrap disabled:opacity-50"
                    :disabled="isFetchingCnpj || !createForm.cnpj"
                    @click="fetchCnpjForCreate"
                  >
                    <span
                      :class="isFetchingCnpj ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-search'"
                      class="size-3.5"
                    />
                    {{ isFetchingCnpj ? 'Buscando...' : 'Buscar' }}
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Razão Social</label>
                <input
                  v-model="createForm.razaoSocial"
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Nome Fantasia</label>
                <input
                  v-model="createForm.nomeFantasia"
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Domínio</label>
                <input
                  v-model="createForm.domain"
                  type="text"
                  placeholder="empresa.com.br"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Email</label>
                <input
                  v-model="createForm.email"
                  type="email"
                  placeholder="contato@empresa.com.br"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Telefone</label>
                <input
                  v-model="createForm.phone"
                  type="text"
                  placeholder="+5511999999999"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Telefone 2</label>
                <input
                  v-model="createForm.telefone2"
                  type="text"
                  placeholder="+5511999999999"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Website</label>
                <input
                  v-model="createForm.website"
                  type="text"
                  placeholder="https://empresa.com.br"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
            </div>
          </div>

          <!-- Endereço -->
          <div>
            <h3 class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider mb-3">Endereço</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="sm:col-span-2">
                <label class="block text-xs text-n-slate-9 mb-1">Endereço</label>
                <input
                  v-model="createForm.endereco"
                  type="text"
                  placeholder="Rua, número, complemento"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Bairro</label>
                <input
                  v-model="createForm.bairro"
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Cidade</label>
                <input
                  v-model="createForm.cidade"
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">UF</label>
                <input
                  v-model="createForm.uf"
                  type="text"
                  maxlength="2"
                  placeholder="SP"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">CEP</label>
                <input
                  v-model="createForm.cep"
                  type="text"
                  maxlength="10"
                  placeholder="00000-000"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
            </div>
          </div>

          <!-- Classificação -->
          <div>
            <h3 class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider mb-3">Classificação</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Setor</label>
                <select
                  v-model="createForm.sector"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                >
                  <option value="">Selecione...</option>
                  <option v-for="s in DEFAULT_SECTORS" :key="s" :value="s">{{ s }}</option>
                </select>
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Porte</label>
                <select
                  v-model="createForm.companySize"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                >
                  <option value="">Selecione...</option>
                  <option v-for="sz in COMPANY_SIZES" :key="sz.key" :value="sz.key">{{ sz.label }}</option>
                </select>
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Lifecycle</label>
                <div class="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-3 text-n-slate-11 cursor-not-allowed">
                  <span class="text-xs font-semibold px-2 py-0.5 rounded-md bg-n-slate-3 text-n-slate-12">Lead</span>
                  <span class="text-[10px] text-n-slate-9 ml-auto">Definido pelos deals</span>
                </div>
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Receita Anual (R$)</label>
                <input
                  v-model="createForm.annualRevenue"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Capital Social (R$)</label>
                <input
                  v-model="createForm.capitalSocial"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
            </div>
          </div>

          <!-- Dados da Receita Federal -->
          <div>
            <h3 class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider mb-3">Dados da Receita Federal</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Natureza Jurídica</label>
                <input
                  v-model="createForm.naturezaJuridica"
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Situação Cadastral</label>
                <input
                  v-model="createForm.situacaoCadastral"
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Data Abertura</label>
                <input
                  v-model="createForm.dataAbertura"
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">Porte (Receita)</label>
                <input
                  v-model="createForm.porteReceita"
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-9 mb-1">CNAE Principal</label>
                <input
                  v-model="createForm.cnaePrincipal"
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none"
                />
              </div>
              <div class="sm:col-span-2">
                <label class="block text-xs text-n-slate-9 mb-1">Quadro Societário</label>
                <textarea
                  v-model="createForm.quadroSocietario"
                  rows="2"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none resize-none"
                />
              </div>
            </div>
          </div>

          <!-- Descrição -->
          <div>
            <label class="block text-xs text-n-slate-9 mb-1">Descrição</label>
            <textarea
              v-model="createForm.description"
              rows="3"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12 focus:ring-2 focus:ring-n-iris-7 focus:border-transparent outline-none resize-none"
            />
          </div>
        </div>

        <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-slate-3">
          <button
            class="px-4 py-2 text-sm rounded-lg bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4 transition-colors"
            @click="closeCreateModal"
          >
            Cancelar
          </button>
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg bg-n-iris-9 text-white hover:bg-n-iris-10 transition-colors disabled:opacity-50"
            :disabled="!createForm.name || isCreating"
            @click="submitCreate"
          >
            {{ isCreating ? 'Criando...' : 'Criar Empresa' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
